/**
 * (owner, Sep 16) The mechanic's trade.
 *
 * The shift (`content/jobs/mechanic_shift.json`, kind work / `mechanic`): at any repair shop, customers' cars pull into
 * the yard with a third to two thirds of their hp gone; the toolbox mends them (game.ts stepRepair) and every point of
 * damage mended on a car that is not your own is paid on the spot — in the yard or out on the road, as long as the shift
 * is on. A car done drives off. The shop lends a toolbox to anyone who comes without one, and takes it back at clock-out.
 *
 * The payroll (`content/defences/mechanic.json`, kind `mechanic`): a repair shop you own can hire one. He stands in the
 * yard and works on whatever stands there with nobody in it and no hook on it: your own cars and any wreck nobody owns
 * (towed in) for nothing, at a third of the toolbox's pace (a wreck: three minutes to a car again); another player's car
 * for the garage's price — the repair scale pro rata to the damage as the work goes, the rebuild price at the end — taken
 * from their pocket into the till while they stand by (no cash, no work). What he does for walk-ins pays `earns` into
 * the till every midnight. Wage like a guard's; unpaid, he walks off the book (threats.ts does the same
 * for guards).
 */
import { count as countAch } from './achievements';
import type { PlacedBuilding } from '../world/buildings';
import { doorstep, powered, type Property } from './property';
import { makePerson, type VehicleEntity } from './entities';
import type { PlayerState } from './game';
import { Game, REPAIR_SECONDS } from './game';
import { isRepairShop, vehicleMaxHp, vehiclePrice } from './shop';
import type { ActiveJob } from './jobs';

/** seconds between customers, and how many wait at once */
export const CUSTOMER_EVERY = 30, CUSTOMERS_MAX = 2;
/** seconds a finished customer sits before driving off */
const LEAVE_S = 6;
/** the yard: this far from the building's walls */
export const YARD = 18;
/** the payroll mechanic's pace against the toolbox's */
const NPC_PACE = 1 / 3;
/** seconds of his work a wreck takes to be a car again (at his pace: three minutes) */
export const REBUILD_S = 60;

const inYard = (b: PlacedBuilding, x: number, y: number): boolean => x > b.x - YARD && x < b.x + b.w + YARD && y > b.y - YARD && y < b.y + b.h + YARD;

/** the customers' cars on this mechanic's shift */
export function customers(game: Game, st: PlayerState): VehicleEntity[] { return game.vehicles.filter((v) => v.customer === st.person.id); }

/** the shift, once the mechanic is at the shop: customers arrive, the done ones leave */
export function stepMechanicShift(game: Game, st: PlayerState, j: ActiveJob, dt: number): void {
  const p = st.person, w = j.work!;
  const here = Math.hypot(p.x - j.from.x, p.y - j.from.y) < 40;
  const cars = customers(game, st);
  // a car driven away from the yard is no customer any more (somebody took it); a done one drives off after a while
  for (let i = game.vehicles.length - 1; i >= 0; i--) {
    const v = game.vehicles[i]; if (v.customer !== p.id) continue;
    if (v.driver || v.wrecked || Math.hypot(v.x - j.from.x, v.y - j.from.y) > 80) { v.customer = undefined; v.customerDone = undefined; continue; }
    if (v.customerDone === undefined && vehicleMaxHp(v) - v.hp < 0.5 && st.repairing?.vid !== v.id) { v.customerDone = game.time; continue; }
    if (v.customerDone !== undefined && game.time - v.customerDone >= LEAVE_S && !v.passengers.length) {
      game.vehicles.splice(i, 1);
      game.say(`The ${v.def.name.toLowerCase()} drives off — ${cars.length - 1} waiting`, p);
    }
  }
  if (!here) return;
  w.at += dt;
  const waiting = cars.filter((v) => v.customerDone === undefined).length;
  if (waiting >= CUSTOMERS_MAX || w.at < (w.units === 0 && waiting === 0 ? 3 : CUSTOMER_EVERY)) return; // (the first one pulls in at once)
  w.at = 0;
  const defs = game.reg.all('vehicle').filter((d) => d.class === 'car' && d.tags.includes('civilian') && !d.tags.includes('police') && !d.tags.includes('emergency'));
  const def = game.npcRng.pick(defs); if (!def) return;
  const n0 = game.vehicles.length; game.spawnVehicleNearPlayer(p, def.id);
  const v = game.vehicles.length > n0 ? game.vehicles[game.vehicles.length - 1] : undefined; if (!v) return;
  const max = vehicleMaxHp(v);
  v.hp = Math.round(max * (0.25 + game.npcRng.float() * 0.4)); v.home = 'lost'; v.fuel = 0.5; v.customer = p.id; v.owner = undefined;
  game.events.push({ kind: 'click', x: v.x, y: v.y });
  game.say(`A customer pulls in: ${def.name}, ${Math.round((v.hp / max) * 100)} % — F beside it with the toolbox`, p);
}

/** pay for the damage mended on somebody else's car while on the shift: $perTile a point (double on the introduction) */
export function payRepair(game: Game, st: PlayerState, v: VehicleEntity, restored: number, finished: boolean): void {
  const j = st.job; const w = j?.work; if (!j || !w || w.kind !== 'mechanic' || restored <= 0.5 || v.owner === st.token) return;
  const def = game.reg.get('job', j.id); if (!def) return;
  const rate = def.pay.perTile * (j.steps ? 2 : 1); // (an introduction pays double: the steps ride along with it — jobs.ts accept)
  const paid = Math.round(restored * rate);
  if (paid <= 0) return;
  st.person.items.cash = (st.person.items.cash ?? 0) + paid; countAch(game, st, 'earned', paid);
  w.earned += paid; if (finished) w.units++;
  game.events.push({ kind: 'cash', x: st.person.x, y: st.person.y, cls: 'in' });
  game.say(`${finished ? `${v.def.name} done` : 'Tools down'} — $${paid} for ${Math.round(restored)} points mended`, st.person);
}

/** the shop's own mechanic: stands in the yard, mends the owner's cars there; walks off unpaid; earns his keep at midnight */
export function stepMechanics(game: Game, dt: number): void {
  for (const prop of game.props.values()) {
    const d = prop.defences.find((x) => game.reg.get('defence', x.id)?.kind === 'mechanic'); if (!d) continue;
    const [bx, by] = prop.key.split(',').map(Number);
    const b = game.world.buildingAt(bx + 0.5, by + 0.5); if (!b) continue; // (chunk not live: he sleeps)
    const def = game.reg.get('defence', d.id)!;
    let m = game.people.find((q) => q.mechanic === prop.key);
    if (!powered(prop)) {
      prop.defences.splice(prop.defences.indexOf(d), 1);
      if (m) { m.mechanic = undefined; m.role = undefined; m.walker = { dx: 0, dy: 0, think: 0, idle: 1 }; }
      const o = game.ownerOnline(prop); if (o) game.say(`Your unpaid ${def.name.toLowerCase()} walked off the ${prop.name.toLowerCase()}`, o.person);
      continue;
    }
    const door = doorstep(b, game.seed);
    const near = [...game.players.values()].some((st) => Math.hypot(st.person.x - door.x, st.person.y - door.y) < 140);
    if (!m) {
      if (!near) continue;
      m = makePerson(door.x + 2.5, door.y, false); m.role = 'mechanic'; m.mechanic = prop.key; m.walker = { dx: 0, dy: 0, think: 0, idle: 1 };
      game.people.push(m);
    }
    if (m.dead) { m.mechanic = undefined; continue; } // (a new one is hired by the book next time somebody is near — the wage keeps him)
    // the cars in the yard, nobody in them, not on a hook: the owner's (and a wreck nobody owns, towed in) for nothing;
    // another player's for the garage's price, taken from their pocket as the work goes — they have to be here to pay
    const team = Game.teamOwner(prop), o = game.ownerOnline(prop);
    const tick = Math.floor(game.time * 2) !== Math.floor((game.time - dt) * 2);
    for (const v of game.vehicles) {
      if (v.driver || v.alt > 0.5 || v.towedBy !== undefined || v.def.class === 'train' || !inYard(b, v.x, v.y)) continue;
      const max = vehicleMaxHp(v); if (!v.wrecked && v.hp >= max) continue;
      const mine = v.owner === undefined || v.owner === prop.owner || (!!team && [...game.players.values()].some((s) => s.token === v.owner && game.teamOf(s.person.id)?.name === team));
      let payer: PlayerState | undefined;
      if (!mine) {
        payer = [...game.players.values()].find((s) => s.token === v.owner && Math.hypot(s.person.x - v.x, s.person.y - v.y) < 40);
        if (!payer) continue; // (the owner is not here to pay: the car waits)
      }
      if (v.wrecked) {
        // a rebuild takes a while; another player's is paid at the end, the garage's rebuild price, or it stays a wreck
        v.rebuildT = (v.rebuildT ?? 0) + dt * NPC_PACE;
        if (tick && game.npcRng.chance(0.4)) game.events.push({ kind: 'repair', x: v.x, y: v.y, cls: 'grinder' });
        if (v.rebuildT < REBUILD_S) continue;
        if (payer) {
          const price = game.rebuildPrice(v), cash = payer.person.items.cash ?? 0;
          if (cash < price) { if (tick) v.rebuildT = REBUILD_S; if (!v.bill) { v.bill = 1; game.say(`${prop.name}: the rebuild is $${price} — you have $${cash}; the wreck waits`, payer.person); } continue; }
          payer.person.items.cash = cash - price; prop.cash += price; v.bill = undefined;
          game.say(`${prop.name}: ${v.def.name} rebuilt — $${price} paid`, payer.person); if (o) game.say(`Your mechanic rebuilt a customer's ${v.def.name.toLowerCase()} — $${price} into the till`, o.person);
        } else if (o) game.say(`Your mechanic at ${prop.name} has rebuilt the ${v.def.name.toLowerCase()}`, o.person);
        v.rebuildT = undefined; game.rebuild(v); game.events.push({ kind: 'click', x: v.x, y: v.y });
        continue;
      }
      const step = Math.min(max - v.hp, (max / REPAIR_SECONDS) * NPC_PACE * dt);
      if (payer) {
        // the garage's price for a full repair, pro rata to the damage, taken in whole dollars as they fall due
        const price = vehiclePrice(v.def) * game.reg.economy.tuning.repair;
        v.bill = (v.bill ?? 0) + (step / max) * price;
        const due = Math.floor(v.bill), cash = payer.person.items.cash ?? 0;
        if (due >= 1) { if (cash < due) { if (Math.floor(game.time / 10) !== Math.floor((game.time - dt) / 10)) game.say(`${prop.name}: no cash, no work — $${Math.round(price * (1 - v.hp / max))} more to finish the ${v.def.name.toLowerCase()}`, payer.person); v.bill -= (step / max) * price; continue; } payer.person.items.cash = cash - due; prop.cash += due; v.bill -= due; }
      }
      v.hp = Math.min(max, v.hp + step);
      if (max - v.hp < 0.5) v.hp = max;
      if (tick && game.npcRng.chance(0.3)) game.events.push({ kind: 'repair', x: v.x, y: v.y, cls: 'wrench' });
      if (v.hp >= max) {
        if (payer) { const paid = Math.round(v.bill ?? 0); if (paid >= 1) { payer.person.items.cash = (payer.person.items.cash ?? 0) - paid; prop.cash += paid; } v.bill = undefined; game.say(`${prop.name}: ${v.def.name} good as new — paid the mechanic`, payer.person); if (o) game.say(`Your mechanic fixed a customer's ${v.def.name.toLowerCase()} at ${prop.name}`, o.person); }
        else if (o) game.say(`Your mechanic at ${prop.name} has the ${v.def.name.toLowerCase()} good as new`, o.person);
      }
    }
  }
}

/** what the payroll mechanics earned today: added to each shop's income at midnight (dailyUpkeep) */
export function mechanicEarns(game: Game, prop: Property): number {
  let sum = 0;
  for (const d of prop.defences) { const def = game.reg.get('defence', d.id); if (def?.kind === 'mechanic' && powered(prop)) sum += def.earns ?? 0; }
  return sum;
}

/** can this building take a mechanic on the payroll: a repair shop */
export function mechanicShop(b: PlacedBuilding): boolean { return isRepairShop(b); }
